/**
 * Shared helper functions for Rider pages (Probate, Guardianship, Trustee, VA).
 * Reduces cognitive complexity by extracting endorsement field logic.
 */

import { applyFiduciarySliceForApproveBody } from "../../SHARED/Bond Retrieval/bondRetrievalApiMappers";

/**
 * Determines the "from" value based on endorsement type
 */
export const getEndorsementFromValue = (log, type) => {
  const isAmountType = ["Increase", "Decrease"].includes(type);
  const isCountyType = type === "Name_Change_County";

  if (isAmountType) {
    return log?.endorsement_from_amount != null
      ? Number(log.endorsement_from_amount).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        })
      : "";
  }

  if (isCountyType) {
    return log?.endorsement_from_county ?? "";
  }

  return log?.endorsement_from_principal ?? "";
};

/**
 * Determines the "to" value based on endorsement type
 */
export const getEndorsementToValue = (log, type) => {
  const isAmountType = ["Increase", "Decrease"].includes(type);
  const isCountyType = type === "Name_Change_County";

  if (isAmountType) {
    return log?.endorsement_to_amount != null
      ? Number(log.endorsement_to_amount).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        })
      : "";
  }

  if (isCountyType) {
    return log?.endorsement_to_county ?? "";
  }

  return log?.endorsement_to_principal ?? "";
};

/**
 * Determines the relevant date for endorsement based on log count
 */
export const getEndorsementRelevantDate = (bondData, endorsementLogs) => {
  if (!bondData || endorsementLogs.length === 0) return "";

  if (endorsementLogs.length === 1) {
    return endorsementLogs[0].endorsed_at || bondData.created_at || "";
  }

  if (endorsementLogs.length > 1) {
    return endorsementLogs[1].endorsed_at;
  }

  return "";
};

/** Match Power Automate ``concat_all_principal_names`` for rider / VA fiduciary lines. */
export function formatConcatenatedPrincipalNames(names) {
  const cleaned = (names || [])
    .map((n) => String(n ?? "").trim())
    .filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return cleaned.join(" & ");
  return `${cleaned.slice(0, -1).join(", ")}, & ${cleaned[cleaned.length - 1]}`;
}

/**
 * When one principal is renamed on a multi-fiduciary bond, rider From/To must list all
 * fiduciaries (e.g. "Tommy & Johnny" → "Tommy & John"). Logs may store only the changed name.
 */
export function enrichMultiPrincipalNameChangeFields(
  fields,
  bondData,
  endorsementLog,
  principalMeta
) {
  if (
    fields?.type !== "Name_Change_Principal" ||
    !principalMeta ||
    !endorsementLog ||
    !bondData
  ) {
    return fields;
  }
  const list = bondData[principalMeta.listKey];
  if (!Array.isArray(list) || list.length <= 1) {
    return fields;
  }
  const fromSingle = (
    endorsementLog.endorsement_from_principal ||
    fields.from ||
    ""
  ).trim();
  const toSingle = (
    endorsementLog.endorsement_to_principal ||
    fields.to ||
    ""
  ).trim();
  if (!fromSingle || !toSingle) {
    return fields;
  }
  if (
    String(fields.from || "").includes("&") &&
    String(fields.to || "").includes("&")
  ) {
    return fields;
  }
  const { nameField } = principalMeta;
  const names = list.map((row) => String(row?.[nameField] ?? "").trim());
  let idx = names.indexOf(toSingle);
  if (idx < 0) {
    idx = names.indexOf(fromSingle);
  }
  if (idx < 0) {
    return fields;
  }
  const fromNames = names.map((n, i) => (i === idx ? fromSingle : n));
  const toNames = names.map((n, i) => (i === idx ? toSingle : n));
  return {
    ...fields,
    from: formatConcatenatedPrincipalNames(fromNames),
    to: formatConcatenatedPrincipalNames(toNames),
  };
}

/** ISO date (yyyy-mm-dd) for HTML date inputs from API timestamps. */
export const toDateInputValue = (dateStr) => {
  if (!dateStr) return "";
  const dateOnly = String(dateStr).split("T")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : "";
};

/**
 * Builds the complete endorsement fields object from bond data and logs.
 * Replaces the complex logic in useEffect across all Rider pages.
 */
/** Prefill rider endorsement section from Release Rider dialog when no log exists yet. */
export function buildEndorsementFieldsFromDialogDraft(bondData, draft) {
  if (!draft?.type) return null;
  const fromAmount =
    draft.fromAmount != null ? draft.fromAmount : bondData?.bond_amount;
  const log = {
    endorsement_type: draft.type,
    endorsement_from_amount: fromAmount,
    endorsement_to_amount: draft.toAmount,
    endorsed_at: draft.endorsedAt || new Date().toISOString(),
  };
  return buildEndorsementFields(bondData, [log]);
}

/** Logs win; otherwise apply navigation draft from the endorsement dialog. */
export function resolveEndorsementFieldsForRider(
  bondData,
  endorsementLogs,
  dialogDraft
) {
  if (bondData && endorsementLogs.length > 0) {
    return buildEndorsementFields(bondData, endorsementLogs);
  }
  const fromDraft = buildEndorsementFieldsFromDialogDraft(bondData, dialogDraft);
  if (fromDraft?.type) {
    return fromDraft;
  }
  return buildEndorsementFields(bondData, endorsementLogs);
}

export const buildEndorsementFields = (bondData, endorsementLogs) => {
  if (!bondData || endorsementLogs.length === 0) {
    return {
      date: "",
      type: "",
      from: "",
      to: "",
      endorsement_from_county: "",
      endorsement_to_county: "",
      effective: "",
      signed_date: "",
    };
  }

  const relevantDate = getEndorsementRelevantDate(bondData, endorsementLogs);
  const now = new Date();
  const signedDateISO = now.toISOString().split("T")[0];

  const currentType = endorsementLogs[0]?.endorsement_type || "";
  const isCountyType = currentType === "Name_Change_County";

  const fromValue = getEndorsementFromValue(endorsementLogs[0], currentType);
  const toValue = getEndorsementToValue(endorsementLogs[0], currentType);

  return {
    date: toDateInputValue(relevantDate),
    type: currentType,
    from: fromValue,
    to: toValue,
    endorsement_from_county: isCountyType
      ? endorsementLogs[0]?.endorsement_from_county ?? ""
      : "",
    endorsement_to_county: isCountyType
      ? endorsementLogs[0]?.endorsement_to_county ?? ""
      : "",
    effective: toDateInputValue(relevantDate),
    signed_date: signedDateISO,
  };
};

/** Normalize GET /endorsement-logs/ response to a sorted-newest-first array. */
export function normalizeEndorsementLogsResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.items)) return response.items;
  return [];
}

/**
 * Extract day, month (name), year from a date string
 */
export const extractDayMonthYear = (dateString) => {
  if (!dateString) return { day: "", month: "", year: "" };

  const dateOnly = dateString.split("T")[0];
  const [year, month, day] = dateOnly.split("-");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return {
    day: String(Number.parseInt(day, 10)).padStart(2, "0"),
    month: months[Number.parseInt(month, 10) - 1],
    year: String(Number.parseInt(year, 10)),
  };
};

/**
 * Format ISO date string to US format (MM/DD/YYYY)
 */
export const formatToUSDate = (dateStr) => {
  if (!dateStr) return "";

  const dateOnly = dateStr.split("T")[0];
  const [year, month, day] = dateOnly.split("-");

  const mm = String(Number.parseInt(month, 10)).padStart(2, "0");
  const dd = String(Number.parseInt(day, 10)).padStart(2, "0");
  const yyyy = String(Number.parseInt(year, 10));

  return `${mm}/${dd}/${yyyy}`;
};

/** Parse a currency-formatted string to a number, or null if empty/invalid. */
export const parseCurrency = (str) => {
  if (!str) return null;
  const cleaned = String(str).replace(/[^0-9.-]+/g, "");
  const value = cleaned ? Number(cleaned) : null;
  return Number.isNaN(value) ? null : value;
};

/** Match endorsement log From to a principal row for name-change riders. */
export function resolvePrincipalIdForPrincipalRider(
  endorsementLog,
  principals,
  { idField, nameField }
) {
  if (!Array.isArray(principals) || principals.length === 0) {
    return undefined;
  }
  const from = (endorsementLog?.endorsement_from_principal || "").trim();
  if (from) {
    const match = principals.find(
      (row) => (row?.[nameField] || "").trim() === from
    );
    if (match?.[idField] != null) {
      return match[idField];
    }
  }
  return principals[0]?.[idField];
}

/** Single fiduciary name for DB row updates — not enriched multi-principal lines. */
export function resolvePrincipalRowUpdatedName(endorsementFields, endorsementLog) {
  const fromLog = (endorsementLog?.endorsement_to_principal || "").trim();
  if (fromLog && !fromLog.includes("&")) {
    return fromLog;
  }
  const fromFields = (endorsementFields?.to || "").trim();
  if (fromFields && !fromFields.includes("&")) {
    return fromFields;
  }
  return "";
}

/** @deprecated Use resolvePrincipalIdForPrincipalRider with trustee list. */
export function resolveTrusteeIdForPrincipalRider(endorsementLog, trustees) {
  return resolvePrincipalIdForPrincipalRider(endorsementLog, trustees, {
    idField: "trustee_id",
    nameField: "name",
  });
}

/** PATCH body fragment: update one principal name in the bond retrieve list. */
export function buildPrincipalListPatchExtras(
  endorsementFields,
  bondData,
  endorsementLog,
  { listKey, idField, nameField }
) {
  if (endorsementFields?.type !== "Name_Change_Principal") {
    return {};
  }
  const list = bondData?.[listKey];
  if (!Array.isArray(list) || list.length === 0) {
    return {};
  }
  const toName = resolvePrincipalRowUpdatedName(endorsementFields, endorsementLog);
  if (!toName) {
    return {};
  }
  const principalId = resolvePrincipalIdForPrincipalRider(
    endorsementLog,
    list,
    { idField, nameField }
  );
  if (principalId == null) {
    return {};
  }
  return {
    [listKey]: list.map((row) =>
      row[idField] === principalId ? { ...row, [nameField]: toName } : row
    ),
  };
}

/** County for PATCH: use endorsement To county when rider type is county change. */
export function resolveRiderPatchCounty(endorsementFields, formCounty) {
  if (endorsementFields?.type === "Name_Change_County") {
    const toCounty = (
      endorsementFields.endorsement_to_county ||
      endorsementFields.to ||
      ""
    ).trim();
    if (toCounty) {
      return toCounty;
    }
  }
  return formCounty;
}

/**
 * Builds the POST body for approving a rider after PATCH + retrieve.
 * @param {object} data - Full bond object from retrieve after PATCH
 * @param {object} endorsementFields - Rider endorsement form state
 */
export const buildRiderApprovePayload = (data, endorsementFields, options = {}) => {
  const bondKind = options.bondKind;
  const slicedData =
    bondKind != null
      ? applyFiduciarySliceForApproveBody(data, bondKind)
      : data;

  const { day, month, year } = extractDayMonthYear(endorsementFields.date);
  const {
    day: signed_day,
    month: signed_month,
    year: signed_year,
  } = extractDayMonthYear(endorsementFields.signed_date);

  const riderData = {
    day,
    month,
    year,
    type: endorsementFields.type,
    from:
      endorsementFields.endorsement_from_county || endorsementFields.from,
    to: endorsementFields.endorsement_to_county || endorsementFields.to,
    effective: formatToUSDate(endorsementFields.effective),
    signed_day,
    signed_month,
    signed_year,
  };

  const principalId = options.principalId ?? options.trusteeId;
  if (endorsementFields.type === "Name_Change_Principal") {
    const rowUpdatedName = resolvePrincipalRowUpdatedName(
      endorsementFields,
      options.endorsementLog
    );
    if (rowUpdatedName) {
      riderData.principal_updated_name = rowUpdatedName;
    }
    if (principalId != null) {
      riderData.principal_id = principalId;
      if (options.bondKind === "trustee") {
        riderData.trustee_id = principalId;
      }
    }
  }

  return {
    ...slicedData,
    bondNumber: slicedData.bond_number?.bondno_id,
    is_rider: true,
    rider_data: riderData,
  };
};
